import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { FaPlay, FaImage, FaHeart, FaDownload, FaExpand, FaTimes } from 'react-icons/fa';

const Feed = () => {
  const [feedItems, setFeedItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'image', 'video'
  const [isMobile, setIsMobile] = useState(false);
  const observerTarget = useRef(null);
  const ITEMS_PER_PAGE_DESKTOP = 50;
  const ITEMS_PER_PAGE_MOBILE = 4;

  // Load feed items from demo content folder
  const loadFeedItems = useCallback(() => {
    const items = [];
    
    // Load demo content from assets folder
    try {
      const demoContent = [
        {
          id: 'demo-img-1',
          type: 'image',
          src: require('../../assets/demo-content/1.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
          title: 'AI-Generated Product Showcase',
          description: 'Professional product photography with AI',
          likes: 124,
          downloads: 42,
        },
        {
          id: 'demo-vid-1',
          type: 'video',
          src: require('../../assets/demo-content/1.mp4'),
          thumbnail: require('../../assets/demo-content/1.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
          title: 'Dynamic Product Video',
          description: 'Engaging video content for social media',
          likes: 256,
          downloads: 87,
        },
        {
          id: 'demo-img-2',
          type: 'image',
          src: require('../../assets/demo-content/2.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
          title: 'Fashion Model Photography',
          description: 'Stunning AI-powered fashion imagery',
          likes: 189,
          downloads: 63,
        },
        {
          id: 'demo-vid-2',
          type: 'video',
          src: require('../../assets/demo-content/2.mp4'),
          thumbnail: require('../../assets/demo-content/2.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
          title: 'Lifestyle Video Content',
          description: 'Captivating lifestyle video for your brand',
          likes: 312,
          downloads: 105,
        },
        {
          id: 'demo-img-3',
          type: 'image',
          src: require('../../assets/demo-content/3.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
          title: 'Product in Context',
          description: 'Lifestyle product placement imagery',
          likes: 167,
          downloads: 54,
        },
        {
          id: 'demo-vid-3',
          type: 'video',
          src: require('../../assets/demo-content/3.mp4'),
          thumbnail: require('../../assets/demo-content/3.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
          title: 'Fashion Showcase Video',
          description: 'Dynamic fashion presentation',
          likes: 289,
          downloads: 96,
        },
        {
          id: 'demo-img-4',
          type: 'image',
          src: require('../../assets/demo-content/4.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
          title: 'Elegant Product Display',
          description: 'High-end product photography',
          likes: 203,
          downloads: 71,
        },
        {
          id: 'demo-vid-4',
          type: 'video',
          src: require('../../assets/demo-content/4.mp4'),
          thumbnail: require('../../assets/demo-content/4.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 9).toISOString(),
          title: 'Motion Graphics Promo',
          description: 'Eye-catching promotional video',
          likes: 421,
          downloads: 142,
        },
        {
          id: 'demo-img-5',
          type: 'image',
          src: require('../../assets/demo-content/5.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
          title: 'Creative Composition',
          description: 'Artistic product arrangement',
          likes: 145,
          downloads: 49,
        },
        {
          id: 'demo-vid-5',
          type: 'video',
          src: require('../../assets/demo-content/5.mp4'),
          thumbnail: require('../../assets/demo-content/5.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 11).toISOString(),
          title: 'Style Showcase Reel',
          description: 'Modern fashion video presentation',
          likes: 367,
          downloads: 118,
        },
        {
          id: 'demo-img-6',
          type: 'image',
          src: require('../../assets/demo-content/6.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12).toISOString(),
          title: 'Premium Product Shot',
          description: 'Luxury apparel photography',
          likes: 231,
          downloads: 78,
        },
        {
          id: 'demo-vid-6',
          type: 'video',
          src: require('../../assets/demo-content/6.mp4'),
          thumbnail: require('../../assets/demo-content/6.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 13).toISOString(),
          title: 'Brand Story Video',
          description: 'Compelling brand narrative',
          likes: 445,
          downloads: 156,
        },
        {
          id: 'demo-img-7',
          type: 'image',
          src: require('../../assets/demo-content/7.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
          title: 'Seasonal Collection',
          description: 'Trendy seasonal apparel showcase',
          likes: 195,
          downloads: 67,
        },
        {
          id: 'demo-img-8',
          type: 'image',
          src: require('../../assets/demo-content/8.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 15).toISOString(),
          title: 'Urban Fashion Style',
          description: 'Contemporary streetwear photography',
          likes: 278,
          downloads: 92,
        },
        {
          id: 'demo-img-10',
          type: 'image',
          src: require('../../assets/demo-content/10.jpg'),
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 17).toISOString(),
          title: 'Classic Elegance',
          description: 'Timeless fashion photography',
          likes: 342,
          downloads: 114,
        },
      ];
      items.push(...demoContent);
    } catch (error) {
      console.error('Error loading feed items:', error);
    }
    
    // Sort by timestamp (newest first)
    items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return items;
  }, []);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typical md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize feed
  useEffect(() => {
    const items = loadFeedItems();
    const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP;
    setFeedItems(items);
    setVisibleItems(items.slice(0, itemsPerPage));
    setHasMore(items.length > itemsPerPage);
  }, [loadFeedItems, isMobile]);

  // Infinite scroll observer (desktop only)
  useEffect(() => {
    // Only enable infinite scroll on desktop
    if (isMobile) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, page, isMobile]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      const filteredItems = filter === 'all' 
        ? feedItems 
        : feedItems.filter(item => item.type === filter);
      
      const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP;
      const nextPage = page + 1;
      const startIndex = (nextPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newItems = filteredItems.slice(startIndex, endIndex);
      
      if (newItems.length > 0) {
        setVisibleItems(prev => [...prev, ...newItems]);
        setPage(nextPage);
        setHasMore(endIndex < filteredItems.length);
      } else {
        setHasMore(false);
      }
      
      setLoading(false);
    }, 500);
  }, [feedItems, page, filter, isMobile]);

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPage(1);
    
    const filteredItems = newFilter === 'all' 
      ? feedItems 
      : feedItems.filter(item => item.type === newFilter);
    
    const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP;
    setVisibleItems(filteredItems.slice(0, itemsPerPage));
    setHasMore(filteredItems.length > itemsPerPage);
  };

  // Handle item click to open modal
  const handleItemClick = (item) => {
    setSelectedItem(item);
  };

  // Close modal
  const closeModal = () => {
    setSelectedItem(null);
  };

  // FeedCard Component
  const FeedCard = ({ item, index }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const cardRef = useRef(null);

    return (
      <div
        ref={cardRef}
        className="group relative overflow-hidden rounded-2xl cursor-pointer transform-gpu animate-fadeIn"
        onClick={() => handleItemClick(item)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          // Random heights for masonry effect
          height: `${Math.floor(Math.random() * 150) + 250}px`,
          animationDelay: `${index * 50}ms`,
        }}
      >
        {/* Card Container */}
        <div className="relative w-full h-full bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl border border-white/10 group-hover:border-cyan-400/50 transition-all duration-500 overflow-hidden">
          
          {/* Loading Skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-800 animate-pulse" />
          )}

          {/* Media Content */}
          {item.type === 'image' ? (
            <img
              src={item.src}
              alt={item.title}
              className={`w-full h-full object-cover transition-all duration-700 ${
                isHovered ? 'scale-110' : 'scale-100'
              } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
              loading="lazy"
            />
          ) : (
            <div className="relative w-full h-full">
              <video
                src={item.src}
                className={`w-full h-full object-cover transition-all duration-700 ${
                  isHovered ? 'scale-110' : 'scale-100'
                }`}
                muted
                loop
                playsInline
                preload="metadata"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <FaPlay className="w-6 h-6 text-white ml-1" />
                </div>
              </div>
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

          {/* Type Badge */}
          <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {item.type === 'image' ? (
              <FaImage className="w-3 h-3 text-cyan-400" />
            ) : (
              <FaPlay className="w-3 h-3 text-pink-400" />
            )}
            <span className="text-xs text-white font-medium capitalize">{item.type}</span>
          </div>

          {/* Hover Actions */}
          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm truncate">{item.title}</h3>
            </div>
            
            <div className="flex items-center justify-between">
              {/* Stats */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <FaHeart className="w-3 h-3 text-pink-400" />
                  <span className="text-xs text-gray-300">{item.likes}</span>
                </div>
                <div className="flex items-center gap-1">
                  <FaDownload className="w-3 h-3 text-cyan-400" />
                  <span className="text-xs text-gray-300">{item.downloads}</span>
                </div>
              </div>

              {/* Expand Button */}
              <button 
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleItemClick(item);
                }}
              >
                <FaExpand className="w-3 h-3 text-white" />
              </button>
            </div>
          </div>

          {/* Corner Accents */}
          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-500" />
          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-cyan-400/0 group-hover:border-cyan-400/60 transition-all duration-500" />
        </div>
      </div>
    );
  };

  // Modal Component
  const Modal = ({ item, onClose }) => {
    if (!item) return null;

    return (
      <AnimatePresence>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl animate-fadeIn"
          onClick={onClose}
        >
          <div
            className="relative max-w-6xl w-full max-h-[90vh] bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden border border-white/10 shadow-2xl animate-modal-entrance"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-3 rounded-full bg-black/60 backdrop-blur-md hover:bg-black/80 transition-all duration-300 border border-white/20"
            >
              <FaTimes className="w-5 h-5 text-white" />
            </button>

            {/* Media Content */}
            <div className="relative w-full h-full flex items-center justify-center p-8">
              {item.type === 'image' ? (
                <img
                  src={item.src}
                  alt={item.title}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                />
              ) : (
                <video
                  src={item.src}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg"
                  controls
                  autoPlay
                  loop
                />
              )}
            </div>

            {/* Info Section */}
            <div className="p-6 border-t border-white/10 bg-black/40">
              <h2 className="text-2xl font-bold text-white mb-4">{item.title}</h2>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <FaHeart className="w-4 h-4 text-pink-400" />
                  <span className="text-gray-300">{item.likes} likes</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaDownload className="w-4 h-4 text-cyan-400" />
                  <span className="text-gray-300">{item.downloads} downloads</span>
                </div>
                <div className="ml-auto">
                  <span className="text-gray-400 text-sm">
                    {new Date(item.timestamp).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatePresence>
    );
  };

  return (
    <section className="relative bg-black overflow-hidden py-16 md:py-20">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-purple-900/20"></div>
        <div className="absolute inset-0 bg-grid-cyan bg-[length:60px_60px] opacity-5"></div>
        
        {/* Floating orbs */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-purple-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        {/* <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            See{' '}
            <span className="gradient-text">What's Possible</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Get inspired by these stunning AI-generated images and videos
          </p>
        </motion.div> */}

        <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">
              See{' '}
              <span className="gradient-text">What's Possible</span>
            </h2>
            <p className="text-white/70">AI Created Pics and Videos for Apparels</p>
        </div>

        {/* Filter Tabs - Hidden for now */}
        {/* {false && (
          <div className="flex items-center justify-center gap-4 mb-12">
            {['all', 'image', 'video'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => handleFilterChange(filterType)}
                className={`px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                  filter === filterType
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-400/50'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                {filterType !== 'all' && 's'}
              </button>
            ))}
          </div>
        )} */}

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
          {visibleItems.map((item, index) => (
            <FeedCard key={item.id} item={item} index={index} />
          ))}
        </div>

        {/* Loading Indicator */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="relative">
              <div className="w-12 h-12 rounded-full border-4 border-gray-700 border-t-cyan-400 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-cyan-400/20 animate-pulse"></div>
              </div>
            </div>
          </div>
        )}

        {/* Load More Button for Mobile */}
        {isMobile && hasMore && !loading && (
          <div className="flex items-center justify-center py-12">
            <button
              onClick={loadMoreItems}
              className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-full shadow-lg shadow-cyan-400/50 hover:shadow-cyan-400/70 hover:scale-105 transition-all duration-300 transform"
            >
              Load More
            </button>
          </div>
        )}

        {/* Observer Target (Desktop only) */}
        {!isMobile && <div ref={observerTarget} className="h-10" />}

        {/* No More Items */}
        {/* {!hasMore && visibleItems.length > 0 && (
          <div className="text-center py-12 animate-fadeIn">
            <p className="text-gray-400 text-sm">You've reached the end of the gallery</p>
          </div>
        )} */}

        {/* Empty State */}
        {visibleItems.length === 0 && !loading && (
          <div className="text-center py-20 animate-fadeIn">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <FaImage className="w-12 h-12 text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Loading gallery...</h3>
            <p className="text-gray-400">Please wait while we load the content</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedItem && <Modal item={selectedItem} onClose={closeModal} />}
    </section>
  );
};

export default Feed;

